/**
 * `ConnectApproval` — assistant MCP OAuth consent landing
 * (`/connect/:request_id`, ADR-0261 §2.5).
 *
 * Flow:
 *   1. On mount, parse the `oar_<ULID>` request id from the pathname.
 *      Save a return path to localStorage so a signed-out visitor
 *      can complete sign-in and the first `authenticated` bootstrap
 *      can replay the URL (see `lib/return-path.ts` + `App.tsx`).
 *   2. If the AuthProvider is bootstrapping, wait. If it resolves to
 *      `unauthenticated` or `service_error`, render the Login screen
 *      in place — the saved return path survives the magic-link
 *      round trip.
 *   3. Once authenticated, GET `/v1/oauth/connect/:request_id`:
 *        - 200 → the preview carries the client host, scopes offered,
 *          default-checked set, and whether the user is on an agency
 *          account.
 *        - 404 → request expired / unknown.
 *        - 403 → acting / support session: ask them to sign in with
 *          their own account.
 *        - 503 → feature not available on this account.
 *   4. The user ticks the scopes they want (write scopes default
 *      unchecked; the server gates per role server-side). Allow
 *      POSTs `/approve { scopes }`; Deny POSTs `/deny`. On success
 *      we hand off to the server-returned `redirect_url` which
 *      lands back on the assistant.
 *
 * Copy is pinned per the ADR-0261 §5 handoff — do not reword without
 * updating both the ADR and the pinned test.
 */

import { useCallback, useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAuth } from '../../app/auth-context.js';
import type { View } from '../../app/view-router.js';
import {
  decideConnectRequest,
  previewConnectRequest,
} from '../../api/connected-apps.js';
import type {
  ConnectRequestPreview,
  ConnectScopeOffer,
} from '@sill/types/api';
import { saveReturnPath } from '../../lib/return-path.js';
import { useC } from '../../lib/theme.js';
import { Button, Card, NumLabel } from '../../components/atoms/index.js';
import { Login } from '../login/Login.js';
import { isSafeRedirectUrl } from '../../lib/safe-redirect';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const REQUEST_ID_RE = /^oar_[0-9A-HJKMNP-TV-Z]{26}$/;

export interface ConnectApprovalProps {
  readonly setView: (v: View) => void;
}

type UiState =
  | { readonly kind: 'bootstrap' }
  | { readonly kind: 'need_signin' }
  | { readonly kind: 'previewing'; readonly request_id: string }
  | { readonly kind: 'ready'; readonly request_id: string; readonly preview: ConnectRequestPreview; readonly checked: ReadonlySet<string> }
  | { readonly kind: 'submitting'; readonly request_id: string; readonly preview: ConnectRequestPreview; readonly checked: ReadonlySet<string>; readonly action: 'allow' | 'deny' }
  | { readonly kind: 'done'; readonly client_label: string }
  | { readonly kind: 'expired' }
  | { readonly kind: 'forbidden'; readonly reason: 'acting_session' | 'support_session' | 'home_session_required' | 'other' }
  | { readonly kind: 'disabled' }
  | { readonly kind: 'invalid_id' }
  | { readonly kind: 'too_many_connections'; readonly client_host: string }
  | { readonly kind: 'error'; readonly message: string };

/**
 * The API agent is adding `client_trusted: boolean` and `client_host:
 * string` to `ConnectRequestPreview` in `@sill/types/api`. Until that
 * lands we read the two fields through this narrow local guard so a
 * missing-field preview still typechecks and renders the pre-field
 * (unknown / use `client_label`) UX.
 */
interface PreviewTrustFields {
  readonly client_trusted?: boolean;
  readonly client_host?: string;
  readonly client_kind?: 'cimd' | 'dcr';
  readonly client_name?: string | null;
}

function readTrust(preview: ConnectRequestPreview): {
  trusted: boolean;
  host: string;
  self_registered: boolean;
  self_name: string | null;
} {
  const extra = preview as unknown as PreviewTrustFields;
  const trusted = extra.client_trusted === true;
  const host =
    typeof extra.client_host === 'string' && extra.client_host.length > 0
      ? extra.client_host
      : preview.client_label;
  const self_registered = extra.client_kind === 'dcr';
  const self_name =
    typeof extra.client_name === 'string' && extra.client_name.trim().length > 0
      ? extra.client_name.trim().slice(0, 128)
      : null;
  return { trusted, host, self_registered, self_name };
}

/** Extract the `oar_<ULID>` request id from the current pathname. */
function readRequestId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const path = new URL(window.location.href).pathname;
    const match = /^\/connect\/([^/?#]+)$/.exec(path);
    if (match === null) return null;
    const raw = decodeURIComponent(match[1] ?? '');
    return REQUEST_ID_RE.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function ConnectApproval({ setView }: ConnectApprovalProps): JSX.Element {
  const auth = useAuth();
  const http = useOptionalHttp();
  const C = useC();

  const [state, setState] = useState<UiState>({ kind: 'bootstrap' });
  const [request_id] = useState<string | null>(() => readRequestId());

  // Save the return path exactly once per mount so a signed-out
  // visitor can finish signing in and the authenticated bootstrap
  // replays `/connect/<id>`.
  useEffect(() => {
    if (request_id === null) return;
    saveReturnPath(`/connect/${request_id}`);
  }, [request_id]);

  // Drive the state machine from the auth bootstrap.
  useEffect(() => {
    if (request_id === null) {
      setState({ kind: 'invalid_id' });
      return;
    }
    if (auth.state.kind === 'bootstrap') return;
    if (auth.state.kind === 'unauthenticated' || auth.state.kind === 'service_error') {
      setState({ kind: 'need_signin' });
      return;
    }
    if (auth.state.kind === 'authenticated') {
      setState((prev) =>
        prev.kind === 'bootstrap' || prev.kind === 'need_signin'
          ? { kind: 'previewing', request_id }
          : prev,
      );
    }
  }, [auth.state, request_id]);

  // Fetch the preview once authenticated.
  useEffect(() => {
    if (state.kind !== 'previewing') return;
    if (http === null) return;
    let cancelled = false;
    const rid = state.request_id;
    void (async () => {
      const r = await previewConnectRequest(http, rid);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setState({
          kind: 'ready',
          request_id: rid,
          preview: r.preview,
          checked: new Set(r.preview.default_checked),
        });
      } else if (r.kind === 'expired') {
        setState({ kind: 'expired' });
      } else if (r.kind === 'forbidden') {
        setState({ kind: 'forbidden', reason: r.reason });
      } else if (r.kind === 'disabled') {
        setState({ kind: 'disabled' });
      } else if (r.kind === 'unauthorized') {
        setState({ kind: 'need_signin' });
      } else {
        setState({ kind: 'error', message: r.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, http]);

  const toggle = useCallback((scope: string): void => {
    setState((prev) => {
      if (prev.kind !== 'ready') return prev;
      const next = new Set(prev.checked);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return { ...prev, checked: next };
    });
  }, []);

  const submit = useCallback(
    async (action: 'allow' | 'deny'): Promise<void> => {
      if (state.kind !== 'ready') return;
      if (http === null) return;
      const { request_id: rid, preview, checked } = state;
      setState({ kind: 'submitting', request_id: rid, preview, checked, action });
      const scopes = action === 'allow' ? Array.from(checked) : [];
      const r = await decideConnectRequest(http, rid, { decision: action, scopes });
      if (r.kind === 'ok') {
        // The server already validated redirect_url against the client's
        // registered URIs; this is defence in depth against drift.
        if (!isSafeRedirectUrl(r.redirect_url)) {
          setState({ kind: 'error', message: 'Sill could not hand the connection back to your assistant. Go back to your assistant and try again.' });
          return;
        }
        setState({ kind: 'done', client_label: preview.client_label });
        if (typeof window !== 'undefined') {
          window.location.assign(r.redirect_url);
        }
        return;
      }
      if (r.kind === 'expired') { setState({ kind: 'expired' }); return; }
      if (r.kind === 'forbidden') { setState({ kind: 'forbidden', reason: r.reason }); return; }
      if (r.kind === 'disabled') { setState({ kind: 'disabled' }); return; }
      if (r.kind === 'invalid_scopes') {
        setState({ kind: 'error', message: 'The selected permissions are no longer valid.' });
        return;
      }
      if (r.kind === 'too_many_connections') {
        setState({ kind: 'too_many_connections', client_host: readTrust(preview).host });
        return;
      }
      setState({ kind: 'error', message: r.message });
    },
    [state, http],
  );

  // Signed-out path renders Login in place — the return path is
  // already stored and the authenticated bootstrap replays the URL.
  if (state.kind === 'need_signin') {
    return <Login setView={setView} />;
  }

  return (
    <div
      className="min-h-screen flex items-start justify-center px-4 sm:px-6 lg:px-8 py-10 sm:py-16"
      style={{ background: C.bg }}
    >
      <div style={{ width: '100%', maxWidth: 560 }}>
        <NumLabel n={1}>Connect an assistant</NumLabel>
        {renderBody(state, auth, C, toggle, submit)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function renderBody(
  state: UiState,
  auth: ReturnType<typeof useAuth>,
  C: ReturnType<typeof useC>,
  toggle: (scope: string) => void,
  submit: (action: 'allow' | 'deny') => Promise<void>,
): JSX.Element {
  const email = auth.state.kind === 'authenticated' ? auth.state.user.email : null;

  if (state.kind === 'bootstrap' || state.kind === 'previewing') {
    return (
      <Card className="p-6">
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Loading…
        </div>
      </Card>
    );
  }

  if (state.kind === 'invalid_id' || state.kind === 'expired') {
    return (
      <Card className="p-6">
        <Title>This connection request has expired.</Title>
        <Lead>Go back to your assistant and try again.</Lead>
      </Card>
    );
  }

  if (state.kind === 'forbidden') {
    return (
      <Card className="p-6">
        <Title>Sign in with your own account to connect an assistant.</Title>
        <Lead>
          {state.reason === 'acting_session'
            ? 'You are managing a client right now. Connecting an assistant always uses your own home account — switch back to it and open this link again.'
            : state.reason === 'support_session'
              ? 'This is a Sill support session, which cannot connect assistants. Ask the account owner to open this link.'
              : state.reason === 'home_session_required'
                ? 'This session cannot approve the connection. Sign in with your own home account to continue.'
                : 'This account cannot approve the connection. Sign in with the account the assistant should act as.'}
        </Lead>
      </Card>
    );
  }

  if (state.kind === 'disabled') {
    return (
      <Card className="p-6">
        <Title>Connecting assistants is not available yet.</Title>
        <Lead>Connecting assistants is not switched on for this account.</Lead>
      </Card>
    );
  }

  if (state.kind === 'done') {
    return (
      <Card className="p-6">
        <Title>Connected.</Title>
        <Lead>
          You can close this tab and return to {state.client_label}.
        </Lead>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card className="p-6">
        <Title>Something went wrong.</Title>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5, marginTop: 10 }}>
          {state.message}
        </div>
      </Card>
    );
  }

  if (state.kind === 'too_many_connections') {
    return (
      <Card className="p-6" data-testid="connect-too-many">
        <Title>Too many connections.</Title>
        <Lead>
          This account already has ten connections for {state.client_host}. Revoke
          one in{' '}
          <a
            href="?view=account&account_tab=connected_apps"
            style={{ color: C.text, textDecoration: 'underline' }}
          >
            Account settings → Connected apps
          </a>
          , then try again.
        </Lead>
      </Card>
    );
  }

  // ready | submitting. The exhaustive earlier branches return above
  // for every other `kind`; narrow explicitly so TS can see the
  // `preview` / `checked` destructure is safe.
  if (state.kind !== 'ready' && state.kind !== 'submitting') {
    return (
      <Card className="p-6">
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Loading…
        </div>
      </Card>
    );
  }
  const { preview, checked } = state;
  const submitting = state.kind === 'submitting';
  const action = state.kind === 'submitting' ? state.action : null;
  const { trusted, host, self_registered, self_name } = readTrust(preview);

  return (
    <Card className="p-6">
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 32,
          lineHeight: 1.1,
          letterSpacing: -1,
          color: C.text,
          margin: 0,
        }}
        data-testid="connect-approval-title"
      >
        Let <span style={{ fontWeight: 500 }}>{preview.client_label}</span> use Sill?
      </h1>

      <div className="mt-3 flex items-center gap-2" data-testid="connect-approval-host">
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
          }}
        >
          {host}
        </span>
        {trusted ? (
          <span
            data-testid="connect-approval-trusted-badge"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: C.text,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '2px 6px',
            }}
          >
            Known assistant
          </span>
        ) : null}
      </div>

      <Lead>
        {preview.client_label} wants to work with your Sill account as{' '}
        <span style={{ color: C.text, fontWeight: 500 }}>{email ?? 'you'}</span>. Choose
        what it can do. You can change or revoke this any time in Account
        settings → Connected apps.
      </Lead>

      {!trusted ? (
        <div
          data-testid="connect-approval-unknown-warning"
          role="note"
          className="mt-4 p-4"
          style={{
            border: `1px solid ${C.amber}`,
            background: C.amberDim,
            borderRadius: 2,
          }}
        >
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {self_registered
              ? `This app registered itself${self_name !== null ? ` as "${self_name}"` : ''}.`
              : `Sill does not recognise ${host} yet.`}
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.6,
              marginTop: 6,
            }}
          >
            {self_registered
              ? `Sill cannot verify who runs it. ${host.replace(/^app:/, '')} is where it will send you back. Only continue if you started this connection yourself. Self-registered apps can only read; they cannot change anything.`
              : `Sill shows the app's address, not its name. Only continue if you started this connection yourself from ${host}. Apps Sill does not know can only read; they cannot change anything.`}
          </div>
        </div>
      ) : null}

      <div
        role="group"
        aria-label="Permissions"
        className="mt-6 flex flex-col"
        style={{ borderTop: `1px solid ${C.borderSoft}` }}
      >
        {preview.offered.map((o) => (
          <ScopeRow
            key={o.scope}
            offer={o}
            checked={checked.has(o.scope)}
            disabled={submitting}
            onToggle={() => toggle(o.scope)}
          />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button
          variant="secondary"
          size="md"
          disabled={submitting}
          loading={submitting && action === 'deny'}
          onClick={() => { void submit('deny'); }}
        >
          Deny
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={submitting}
          loading={submitting && action === 'allow'}
          onClick={() => { void submit('allow'); }}
          data-testid="connect-approval-allow"
        >
          Allow
        </Button>
      </div>

      {email !== null ? (
        <div
          className="mt-4"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}
        >
          Signed in as {email}.
        </div>
      ) : null}
    </Card>
  );
}

function ScopeRow({
  offer,
  checked,
  disabled,
  onToggle,
}: {
  readonly offer: ConnectScopeOffer;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  const C = useC();
  const secondary = !offer.requested;
  return (
    <label
      className="flex items-start gap-3 py-3 cursor-pointer"
      style={{
        borderBottom: `1px solid ${C.borderSoft}`,
        opacity: secondary ? 0.75 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        aria-label={offer.label}
        data-scope={offer.scope}
        style={{ marginTop: 3, flexShrink: 0 }}
      />
      <div className="min-w-0">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
          {offer.label}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginTop: 2,
          }}
        >
          {offer.helper}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1.4,
            color: C.dim,
            textTransform: 'uppercase',
            marginTop: 4,
          }}
        >
          {offer.scope}
        </div>
      </div>
    </label>
  );
}

function Title({ children }: { readonly children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <h1
      style={{
        fontFamily: serifFont,
        fontSize: 28,
        lineHeight: 1.1,
        letterSpacing: -0.8,
        color: C.text,
        margin: 0,
      }}
    >
      {children}
    </h1>
  );
}

function Lead({ children }: { readonly children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <p
      style={{
        fontFamily: sansFont,
        fontSize: 14,
        color: C.muted,
        lineHeight: 1.6,
        marginTop: 12,
        marginBottom: 0,
      }}
    >
      {children}
    </p>
  );
}

